'use client';

import { useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';

type Placement = 'hero' | 'mid' | 'footer';

interface Props {
  placement: Placement;
}

export function MoldInspectorGuideClient({ placement }: Props) {
  const searchParams = useSearchParams();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>(
    'idle',
  );
  const [errorMessage, setErrorMessage] = useState('');

  const utmSource = useMemo(
    () => (searchParams?.get('utm_source') || '').toLowerCase(),
    [searchParams],
  );

  async function handleSubmit(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setErrorMessage('Please enter a valid email address.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('/api/subscribe/mold-inspector-guide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName: firstName || null,
          lastName: lastName || null,
          source: `landing-page-${placement}`,
          utmSource: utmSource || null,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          data.error || 'Something went wrong. Please try again.',
        );
      }

      setStatus('success');
      setEmail('');
      setFirstName('');
      setLastName('');
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Something went wrong.',
      );
    }
  }

  if (status === 'success') {
    return (
      <div
        className={`rounded-lg p-6 text-left ${
          placement === 'mid'
            ? 'bg-deep-navy/40 border border-soft-teal/40 text-white'
            : 'bg-soft-teal/10 border border-soft-teal/30 text-deep-navy'
        }`}
      >
        <p className="font-serif font-bold text-lg mb-2">Check your inbox.</p>
        <p className="text-sm leading-relaxed mb-4">
          The Mold Inspector Checklist is on its way. If you do not see it in 5
          minutes, check spam and add{' '}
          <span className="font-medium">hello@moldfreemom.com</span> to your
          contacts.
        </p>
        <p
          className={`text-sm leading-relaxed mb-4 ${
            placement === 'mid' ? 'text-soft-teal-light' : 'text-charcoal'
          }`}
        >
          Want help walking this process inside my recovery program?
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            href="/mold-recovery-programs/guided"
            className={`inline-flex justify-center items-center px-5 py-2.5 rounded-full text-sm font-semibold transition ${
              placement === 'mid'
                ? 'bg-blush text-deep-navy hover:bg-blush-light'
                : 'bg-soft-teal text-white hover:bg-soft-teal-dark'
            }`}
          >
            Explore Guided recovery
          </Link>
          <Link
            href="/book?type=discovery"
            className={`inline-flex justify-center items-center px-5 py-2.5 rounded-full text-sm font-semibold transition ${
              placement === 'mid'
                ? 'border border-white/40 text-white hover:bg-white/10'
                : 'border border-soft-teal text-soft-teal-dark hover:bg-soft-teal/10'
            }`}
          >
            Book a discovery call
          </Link>
        </div>
      </div>
    );
  }

  const isDark = placement === 'mid';

  const inputClass = `w-full px-4 py-3 rounded-full border focus:outline-none focus:ring-2 focus:ring-soft-teal transition ${
    isDark
      ? 'bg-deep-navy/50 border-soft-teal/40 text-white placeholder-soft-teal-light/70'
      : 'bg-white border-[#E0DBD5] text-deep-navy placeholder-charcoal/40'
  }`;

  return (
    <div className="w-full max-w-md mx-auto text-left">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="First name (optional)"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={status === 'loading'}
            className={inputClass}
            autoComplete="given-name"
          />
          <input
            type="text"
            placeholder="Last name (optional)"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={status === 'loading'}
            className={inputClass}
            autoComplete="family-name"
          />
        </div>
        <input
          type="email"
          placeholder="Your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === 'loading'}
          required
          className={inputClass}
          autoComplete="email"
          aria-label="Email address for the mold inspector checklist"
        />

        <p
          className={`text-xs leading-relaxed px-1 ${
            isDark ? 'text-white/60' : 'text-charcoal/55'
          }`}
        >
          I&apos;ll email the checklist and occasional recovery tips. Unsubscribe
          anytime.
        </p>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={status === 'loading'}
          className={`w-full px-6 py-3 rounded-full font-semibold text-base transition ${
            isDark
              ? 'bg-blush text-deep-navy hover:bg-blush-light'
              : 'bg-soft-teal text-white hover:bg-soft-teal-dark shadow-[0_4px_16px_rgba(79,163,165,0.3)]'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {status === 'loading' ? 'Sending...' : 'Send Me the Free Checklist'}
        </button>
      </div>

      {status === 'error' && (
        <p
          className={`text-sm mt-3 ${
            isDark ? 'text-rose-200' : 'text-rose-700'
          }`}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
}
